import styled from "styled-components";
import { pxToRem } from "../../styles/utils";

export const TableContainer = styled.div<{ isExpanded?: boolean; minHeight?: string; remHeight?: number }>`
  position: relative;
  display: flex;
  flex: 1 1 0;
  flex-direction: column;
  justify-content: space-between;
  overflow-x: auto;
  overflow-y: auto;
  scrollbar-width: none;
  scrollbar-gutter: stable;
  border: ${pxToRem(1.25)} solid ${({ theme }) => theme.colors.inkBorder};
  border-radius: ${pxToRem(17.5)};
  background: ${({ theme }) => theme.colors.white};
  box-shadow: none;
  transition: height 0.3s ease;
  max-width: 100%;
  min-width: 0;
  height: ${({ isExpanded }) => (isExpanded ? "100%" : "auto")};
  min-height: ${({ minHeight }) => minHeight || "0"};

  &::-webkit-scrollbar {
    display: none;
  }
`;

export const StyledTable = styled.table<{ $layoutLocked?: boolean }>`
  border-collapse: collapse;
  width: 100%;
  table-layout: ${({ $layoutLocked }) => ($layoutLocked ? "fixed" : "auto")};
`;

export const TableHeader = styled.thead`
  position: sticky;
  top: 0;
  z-index: 10;
  background: ${({ theme }) => theme.colors.white};

  th {
    padding: 0;
    text-align: left;
    color: ${({ theme }) => theme.colors.inkMuted};
    font-size: ${pxToRem(16)};
    font-weight: 500;
    line-height: ${pxToRem(20)};
    white-space: nowrap;
    border-bottom: ${pxToRem(1.25)} solid ${({ theme }) => theme.colors.inkBorder};
    background: ${({ theme }) => theme.colors.white};
  }

  th:first-child > div {
    padding-left: ${pxToRem(25)};
  }

  th:last-child > div {
    padding-right: ${pxToRem(25)};
  }
`;

export const TableRow = styled.tr<{ isExpanded?: boolean }>`
  position: ${({ isExpanded }) => (isExpanded ? "sticky" : "relative")};
  top: ${({ isExpanded }) => (isExpanded ? pxToRem(100) : "auto")};
  background: ${({ theme }) => theme.colors.white};
  z-index: ${({ isExpanded }) => (isExpanded ? 2 : 1)};
  transition: background-color 0.15s ease, top 0.3s ease;

  > td:not([colspan]) {
    background: ${({ isExpanded, theme }) =>
      isExpanded ? theme.colors.brandSubtle : theme.colors.white};
  }

  &:hover > td:not([colspan]) {
    background: ${({ theme }) => theme.colors.brandSubtle};
  }
`;

export const TableBody = styled.tbody`
  tr:last-child td {
    border-bottom: none;
  }
`;

export const TableCell = styled.td<{ isHidden?: boolean; $expandedFlush?: boolean }>`
  height: ${({ $expandedFlush }) => ($expandedFlush ? "auto" : pxToRem(65))};
  padding: ${({ $expandedFlush }) =>
    $expandedFlush ? "0" : `${pxToRem(10)} ${pxToRem(20)}`};
  vertical-align: ${({ $expandedFlush }) => ($expandedFlush ? "top" : "middle")};
  white-space: ${({ $expandedFlush }) => ($expandedFlush ? "normal" : "nowrap")};
  overflow: ${({ $expandedFlush }) => ($expandedFlush ? "visible" : "hidden")};
  color: ${({ theme }) => theme.colors.ink};
  font-size: ${pxToRem(18)};
  font-weight: 400;
  border-bottom: ${pxToRem(1.25)} solid ${({ theme }) => theme.colors.inkBorder};
  background: inherit;
  transition: background-color 0.15s ease;

  &:first-child {
    padding-left: ${({ $expandedFlush }) => ($expandedFlush ? "0" : pxToRem(25))};
  }

  &:last-child {
    padding-right: ${({ $expandedFlush }) => ($expandedFlush ? "0" : pxToRem(25))};
  }

  div.content {
    visibility: ${({ isHidden }) => (isHidden ? "hidden" : "visible")};
    display: flex;
    align-items: center;
    width: 100%;
    min-width: 0;
    overflow: hidden;
    gap: ${pxToRem(10)};
    flex-wrap: nowrap;

    > * {
      min-width: 0;
      max-width: 100%;
    }

    /*
      Текст можно ужать и подрезать многоточием, группу иконок — нельзя:
      обрезанная кнопка для пользователя просто не существует. Пока у неё
      был общий min-width: 0, колонка действий отдавала ширину соседям
      первой и схлопывалась, как только текста становилось слишком много.
    */
    > .table-actions {
      min-width: max-content;
      max-width: none;
    }
  }
`;

export const DragHandleCell = styled(TableCell)`
  width: ${pxToRem(64)};
  padding-left: ${pxToRem(20)};
  padding-right: ${pxToRem(10)};

  div.content {
    justify-content: center;
  }
`;

export const TableHeadCell = styled.div`
  min-height: ${pxToRem(48)};
  display: inline-flex;
  align-items: center;
  gap: ${pxToRem(7.5)};
  padding: ${pxToRem(17.5)} ${pxToRem(20)} ${pxToRem(15)};
  width: 100%;

  span {
    white-space: nowrap;
  }
`;

export const PaginatorWrapper = styled.div`
  margin-top: auto;
  position: sticky;
  bottom: 0;
  background: ${({ theme }) => theme.colors.white};
  z-index: 2;
`;

export const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100%;
  padding: ${pxToRem(10)} 0;
`;

export const BackgroundLoadingIndicator = styled.div`
  position: absolute;
  top: ${pxToRem(14)};
  right: ${pxToRem(14)};
  z-index: 12;
  display: flex;
  align-items: center;
  justify-content: center;
  width: ${pxToRem(28)};
  height: ${pxToRem(28)};
  pointer-events: none;
`;

export const FilterIcon = styled.svg`
  box-sizing: border-box;
  width: ${pxToRem(18)};
  height: ${pxToRem(18)};
  padding: ${pxToRem(2)};
  flex: 0 0 auto;
  color: ${({ theme }) => theme.colors.inkMuted};
`;

export const SortIcon = styled.svg`
  width: ${pxToRem(12.5)};
  height: ${pxToRem(15)};
  flex: 0 0 auto;
  color: ${({ theme }) => theme.colors.inkMuted};
  opacity: 0.55;
`;

export const DragHandleButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: ${pxToRem(32)};
  height: ${pxToRem(32)};
  border: none;
  background: transparent;
  color: ${({ theme }) => theme.colors.inkMuted};
  cursor: grab;
  padding: 0;

  &[data-dragging="true"] {
    cursor: grabbing;
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.35;
  }
`;

export const DragHandleIcon = styled.svg`
  width: ${pxToRem(20)};
  height: ${pxToRem(20)};
  flex: 0 0 auto;
  color: inherit;
`;